import { toast } from "sonner";

export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  VALIDATION = 'VALIDATION',
  PERMISSION = 'PERMISSION',
  SYSTEM = 'SYSTEM',
  USER_INPUT = 'USER_INPUT'
}

export interface AppError extends Error {
  type: ErrorType;
  code?: string;
  context?: Record<string, any>;
  userMessage?: string;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: AppError[] = [];

  public static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  public handle(error: unknown, context?: string): AppError {
    const appError = this.normalizeError(error, context);
    
    // Log l'erreur de manière standardisée
    this.logError(appError);
    
    // Affiche un toast approprié selon le type d'erreur
    this.showUserFeedback(appError);
    
    return appError;
  }

  private normalizeError(error: unknown, context?: string): AppError {
    if (error instanceof Error && 'type' in error) {
      return error as AppError;
    }

    let appError: AppError;

    if (error instanceof Error) {
      appError = Object.assign(error, {
        type: this.detectErrorType(error.message),
        context: context ? { source: context } : undefined,
        userMessage: this.getUserMessage(error.message)
      }) as AppError;
    } else {
      appError = {
        name: 'UnknownError',
        message: String(error),
        type: ErrorType.SYSTEM,
        context: context ? { source: context } : undefined,
        userMessage: 'Une erreur inattendue s\'est produite'
      } as AppError;
    }

    return appError;
  }

  private detectErrorType(message: string): ErrorType {
    const msg = message.toLowerCase();
    
    if (msg.includes('fetch') || msg.includes('network') || msg.includes('connexion')) {
      return ErrorType.NETWORK;
    }
    if (msg.includes('auth') || msg.includes('unauthorized') || msg.includes('token')) {
      return ErrorType.AUTH;
    }
    if (msg.includes('validation') || msg.includes('required') || msg.includes('invalid')) {
      return ErrorType.VALIDATION;
    }
    if (msg.includes('permission') || msg.includes('forbidden') || msg.includes('access')) {
      return ErrorType.PERMISSION;
    }
    
    return ErrorType.SYSTEM;
  }

  private getUserMessage(message: string): string {
    const msg = message.toLowerCase();
    
    if (msg.includes('fetch') || msg.includes('network')) {
      return 'Problème de connexion. Vérifiez votre internet.';
    }
    if (msg.includes('unauthorized') || msg.includes('auth')) {
      return 'Vous devez vous reconnecter.';
    }
    if (msg.includes('validation') || msg.includes('required')) {
      return 'Veuillez vérifier les informations saisies.';
    }
    if (msg.includes('permission') || msg.includes('forbidden')) {
      return 'Vous n\'avez pas les droits pour cette action.';
    }
    
    return 'Une erreur s\'est produite. Veuillez réessayer.';
  }

  private logError(error: AppError): void {
    // Log console structuré
    console.group(`🚨 [${error.type}] ${error.name}`);
    console.error('Message:', error.message);
    if (error.context) {
      console.error('Context:', error.context);
    }
    console.error('Stack:', error.stack);
    console.groupEnd();

    // Ajouter au log interne (limité à 100 erreurs)
    this.errorLog.unshift(error);
    if (this.errorLog.length > 100) {
      this.errorLog = this.errorLog.slice(0, 100);
    }
  }

  private showUserFeedback(error: AppError): void {
    const userMessage = error.userMessage || error.message;
    
    switch (error.type) {
      case ErrorType.NETWORK:
        toast.error(userMessage, {
          description: 'Vérifiez votre connexion internet',
          action: {
            label: 'Réessayer',
            onClick: () => window.location.reload()
          }
        });
        break;
        
      case ErrorType.AUTH:
        toast.error(userMessage, {
          description: 'Redirection vers la page de connexion...',
          action: {
            label: 'Se connecter',
            onClick: () => window.location.href = '/auth'
          }
        });
        break;
        
      case ErrorType.VALIDATION:
        toast.warning(userMessage);
        break;
        
      case ErrorType.PERMISSION:
        toast.error(userMessage, {
          description: 'Contactez un administrateur si nécessaire'
        });
        break;
        
      default:
        toast.error(userMessage, {
          description: 'L\'équipe technique a été notifiée'
        });
    }
  }

  public getErrorLog(): AppError[] {
    return [...this.errorLog];
  }

  public clearErrorLog(): void {
    this.errorLog = [];
  }
}

// Factory functions pour créer des erreurs typées
export const createError = {
  network: (message: string, context?: Record<string, any>): AppError => ({
    name: 'NetworkError',
    message,
    type: ErrorType.NETWORK,
    context,
    userMessage: 'Problème de connexion. Vérifiez votre internet.'
  } as AppError),

  auth: (message: string, context?: Record<string, any>): AppError => ({
    name: 'AuthError',
    message,
    type: ErrorType.AUTH,
    context,
    userMessage: 'Vous devez vous reconnecter.'
  } as AppError),

  validation: (message: string, context?: Record<string, any>): AppError => ({
    name: 'ValidationError',
    message,
    type: ErrorType.VALIDATION,
    context,
    userMessage: 'Veuillez vérifier les informations saisies.'
  } as AppError),

  permission: (message: string, context?: Record<string, any>): AppError => ({
    name: 'PermissionError',
    message,
    type: ErrorType.PERMISSION,
    context,
    userMessage: 'Vous n\'avez pas les droits pour cette action.'
  } as AppError),

  system: (message: string, context?: Record<string, any>): AppError => ({
    name: 'SystemError',
    message,
    type: ErrorType.SYSTEM,
    context,
    userMessage: 'Une erreur système s\'est produite.'
  } as AppError)
};

// Instance globale
export const errorHandler = ErrorHandler.getInstance();